import React, { useState, useEffect } from 'react';
import DeviceDetection from '../utils/DeviceDetection';
import './SmartAppBanner.css';

/**
 * SmartAppBanner Component
 *
 * Displays a top banner for mobile/tablet users to open or download the ChillFree TV app
 * Similar to Netflix's smart app banner implementation
 */
const SmartAppBanner = ({ currentPath = '/' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [appInstalled, setAppInstalled] = useState(null); // null = checking, true = installed, false = not installed
  const [deviceType, setDeviceType] = useState('');

  // Check if banner should be shown
  useEffect(() => {
    const checkBannerVisibility = () => {
      // Only show on mobile and tablet devices
      const isMobile = DeviceDetection.isMobile();
      const isIPad = DeviceDetection.isIPad();
      const isAndroidTablet =
        /Android/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent);

      const shouldShow = isMobile || isIPad || isAndroidTablet;
      const deviceTypeDetected = DeviceDetection.getDeviceType();

      setDeviceType(deviceTypeDetected);
      setIsVisible(shouldShow);

      // Check if banner was previously dismissed (session storage)
      const dismissed = sessionStorage.getItem('chillfreetv-banner-dismissed');
      if (dismissed === 'true') {
        setIsDismissed(true);
      }
    };

    checkBannerVisibility();
  }, []);

  // Check if app is installed
  useEffect(() => {
    if (!isVisible || isDismissed) return;

    let cleanup = null;

    const checkAppInstallation = async () => {
      try {
        // For iOS devices
        if (DeviceDetection.isIPhone() || DeviceDetection.isIPad()) {
          console.log('🔍 SmartAppBanner: Checking iOS app installation');
          
          // In Facebook browser, app detection is unreliable, so default to showing "OPEN" button
          // This allows users to try the deep link regardless of detection accuracy
          if (DeviceDetection.isFacebookBrowser()) {
            console.log('📘 SmartAppBanner: Facebook browser detected, assuming app available');
            setAppInstalled(true);
            return;
          }
          
          // Try to open the app with a custom URL scheme for detection
          const appScheme = 'dpro://test'; // Test scheme for detection
          
          // Create a hidden iframe to test the scheme
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = appScheme;
          document.body.appendChild(iframe);

          // Set a timeout to check if the app opened
          const timeout = setTimeout(() => {
            console.log('⏰ SmartAppBanner: iOS detection timeout, assuming not installed');
            setAppInstalled(false);
            if (iframe && iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 2000);

          // If the page becomes hidden (app opened), assume app is installed
          const handleVisibilityChange = () => {
            if (document.hidden) {
              console.log('✅ SmartAppBanner: iOS app detected via visibility change');
              clearTimeout(timeout);
              setAppInstalled(true);
              if (iframe && iframe.parentNode) {
                document.body.removeChild(iframe);
              }
              document.removeEventListener('visibilitychange', handleVisibilityChange);
            }
          };

          document.addEventListener('visibilitychange', handleVisibilityChange);

          // Store cleanup function
          cleanup = () => {
            clearTimeout(timeout);
            if (iframe && iframe.parentNode) {
              document.body.removeChild(iframe);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
        } else if (DeviceDetection.isAndroid()) {
          console.log('🔍 SmartAppBanner: Checking Android app installation');
          
          // In Facebook browser, app detection is unreliable, so default to showing "OPEN" button
          if (DeviceDetection.isFacebookBrowser()) {
            console.log('📘 SmartAppBanner: Facebook browser detected, assuming app available');
            setAppInstalled(true);
            return;
          }
          
          // For Android devices, try intent URL for detection
          const intentUrl = 'intent://test#Intent;package=com.dotstudiopro.greenlight;end';

          try {
            // Try to open the app for detection
            window.location.href = intentUrl;

            // Set a timeout to check if the app opened
            setTimeout(() => {
              console.log('⏰ SmartAppBanner: Android detection timeout, assuming not installed');
              setAppInstalled(false);
            }, 1500);

            // If the page becomes hidden, assume app opened
            const handleVisibilityChange = () => {
              if (document.hidden) {
                console.log('✅ SmartAppBanner: Android app detected via visibility change');
                setAppInstalled(true);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
              }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
          } catch (error) {
            console.log('❌ SmartAppBanner: Android detection failed:', error);
            setAppInstalled(false);
          }
        } else {
          // For other devices (tablets), default to showing "Get" button
          setAppInstalled(false);
        }
      } catch (error) {
        console.warn('App installation check failed:', error);
        setAppInstalled(false);
      }
    };

    // Delay the check slightly to ensure page is loaded
    const timer = setTimeout(checkAppInstallation, 1000);

    return () => {
      clearTimeout(timer);
      if (cleanup) {
        cleanup();
      }
    };
  }, [isVisible, isDismissed]);

  // Handle app opening with deep link to current content
  const handleOpenApp = () => {
    const appUrls = DeviceDetection.getAppStoreUrls();
    
    console.log('🎯 SmartAppBanner: Opening app with deep link to:', currentPath);
    
    // The deep link format uses query parameter: dpro://open?channel=the-she-beast
    // First check if there's already a channel parameter in the current URL
    const urlParams = new URLSearchParams(window.location.search);
    let channelId = urlParams.get('channel');
    let appDeepLink = 'dpro://open';
    
    if (channelId) {
      // Channel ID found in URL parameters
      appDeepLink = `dpro://open?channel=${channelId}`;
      console.log('🔄 SmartAppBanner: Found channel ID in URL params:', channelId);
      console.log('🎯 SmartAppBanner: Deep link with query param:', appDeepLink);
    } else if (currentPath.startsWith('/movie/') || currentPath.startsWith('/series/')) {
      // Extract channel ID from current path /movie/the-she-beast or /series/xyz
      const pathParts = currentPath.split('/');
      channelId = pathParts[pathParts.length - 1]; // Get the channel ID
      appDeepLink = `dpro://open?channel=${channelId}`;
      console.log('🔄 SmartAppBanner: Extracted channel ID from path:', channelId);
      console.log('🎯 SmartAppBanner: Deep link with query param:', appDeepLink);
    } else {
      console.log('⚠️ SmartAppBanner: No channel found in URL params or path format:', currentPath);
    }

    // For Facebook browser, redirect to Safari with the same PDP URL
    // Safari will automatically handle deep linking via Apple App Site Association
    if (DeviceDetection.isFacebookBrowser() || DeviceDetection.isSocialMediaBrowser()) {
      console.log('📘 SmartAppBanner: Social media browser - redirecting to Safari with PDP URL');
      
      // Convert to query parameter format that the app expects
      const safariUrl = channelId 
        ? `https://chillfree.tv/?channel=${channelId}`
        : `https://chillfree.tv/`;
      console.log('🍎 SmartAppBanner: Redirecting to Safari with query format:', safariUrl);
      
      try {
        window.open(safariUrl, '_blank');
      } catch (error) {
        console.error('🍎 SmartAppBanner: Safari redirect failed:', error);
        // Fallback to App Store
        if (DeviceDetection.isIPhone() || DeviceDetection.isIPad()) {
          window.location.href = appUrls.ios;
        } else if (DeviceDetection.isAndroid()) {
          window.location.href = appUrls.android;
        }
      }
    } else {
      // Regular browser (Safari, Chrome) - use direct deep link
      console.log('🌐 SmartAppBanner: Regular browser, using direct deep link');
      
      if (DeviceDetection.isIPhone() || DeviceDetection.isIPad()) {
        console.log('🍎 SmartAppBanner: iOS deep link:', appDeepLink);
        
        try {
          // Create a temporary link and click it (simulates browser behavior)
          const link = document.createElement('a');
          link.href = appDeepLink;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          console.log('🍎 SmartAppBanner: Triggered deep link via temporary link');
          
          // Fallback to App Store if app doesn't open
          setTimeout(() => {
            console.log('🍎 SmartAppBanner: iOS fallback to App Store');
            window.location.href = appUrls.ios;
          }, 2000);
        } catch (error) {
          console.error('🍎 SmartAppBanner: iOS deep link failed:', error);
          window.location.href = appUrls.ios;
        }
      } else if (DeviceDetection.isAndroid()) {
        const intentUrl = `intent://open?channel=${channelId}#Intent;package=com.dotstudiopro.greenlight;S.browser_fallback_url=${encodeURIComponent(
          appUrls.android,
        )};end`;
        console.log('🤖 SmartAppBanner: Android intent:', intentUrl);
        
        try {
          const link = document.createElement('a');
          link.href = intentUrl;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          console.log('🤖 SmartAppBanner: Triggered intent via temporary link');
        } catch (error) {
          console.error('🤖 SmartAppBanner: Android intent failed:', error);
          window.location.href = appUrls.android;
        }
      }
    }
  };

  // Handle app download
  const handleGetApp = () => {
    const appUrls = DeviceDetection.getAppStoreUrls();

    if (DeviceDetection.isIPhone() || DeviceDetection.isIPad()) {
      window.location.href = appUrls.ios;
    } else if (DeviceDetection.isAndroid()) {
      window.location.href = appUrls.android;
    } else {
      window.location.href = appUrls.fallback;
    }
  };

  // Handle banner dismissal
  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('chillfreetv-banner-dismissed', 'true');
  };

  // Don't render if not visible or dismissed
  if (!isVisible || isDismissed) {
    return null;
  }

  return (
    <div className="smart-app-banner">
      <div className="smart-app-banner-content">
        {/* App Icon */}
        <div className="smart-app-banner-icon">
          <img
            src="/assets/icons/mstile-70x70.png"
            alt="ChillFree TV"
            className="app-icon"
            onError={e => {
              // First fallback to app icon
              e.target.src = '/assets/icons/favicon-32x32.png';
              e.target.onerror = e2 => {
                // Final fallback to text icon if both images fail
                e2.target.style.display = 'none';
                e2.target.nextSibling.style.display = 'flex';
              };
            }}
          />
          <div className="app-icon-fallback" style={{ display: 'none' }}>
            📺
          </div>
        </div>

        {/* App Info */}
        <div className="smart-app-banner-info">
          <div className="app-name">ChillFree TV</div>
          <div className="app-description">
            {appInstalled === true
              ? 'Watch this content in the app'
              : 'Get the app for the best experience'}
          </div>
        </div>

        {/* Action Button */}
        <div className="smart-app-banner-actions">
          {appInstalled === null ? (
            // Loading state
            <button className="app-action-button loading" disabled>
              <div className="loading-spinner"></div>
            </button>
          ) : appInstalled === true ? (
            // App is installed - show "Open" button
            <button className="app-action-button open" onClick={handleOpenApp}>
              OPEN
            </button>
          ) : (
            // App is not installed - show "Get" button
            <button className="app-action-button get" onClick={handleGetApp}>
              GET
            </button>
          )}
        </div>

        {/* Dismiss Button */}
        <button
          className="smart-app-banner-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default SmartAppBanner;
